import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { menuData, registeredSlugs } from '../config/menuData.js';
import { filterMenuByRole } from '../config/navAccess.js';
import HelpDrawer from '../help/HelpDrawer.jsx';

const APP_NAME = 'AcademyOS';

// A menu entry resolves to its slug-based route when the page exists; until
// then it falls back to the 404 page so the nav never dead-ends.
function routeFor(slug) {
  if (!slug) return null;
  return registeredSlugs.has(slug) ? `/${slug}` : '/404';
}

function LeafLink({ item, className }) {
  const to = routeFor(item.slug);
  return (
    <NavLink to={to || '/404'} className={className} end>
      {item.icon && <i className={`${item.icon} me-2`} aria-hidden="true" />}
      {item.label}
    </NavLink>
  );
}

function MenuItem({ item }) {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  if (!hasChildren) {
    return (
      <li className="nav-item">
        <LeafLink item={item} className="nav-link text-white" />
      </li>
    );
  }

  const id = `menu-${item.label.replace(/\W+/g, '_')}`;
  return (
    <li className="nav-item dropdown">
      <a
        className="nav-link dropdown-toggle text-white"
        href="#"
        id={id}
        role="button"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {item.icon && <i className={`${item.icon} me-2`} aria-hidden="true" />}
        {item.label}
      </a>
      <ul className="dropdown-menu" aria-labelledby={id}>
        {item.children.map((child) => (
          <li key={child.label}>
            <LeafLink item={child} className="dropdown-item" />
          </li>
        ))}
      </ul>
    </li>
  );
}

export default function AppNavbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const [helpOpen, setHelpOpen] = useState(false);

  // Only show nav entries the current visitor can actually open (mirrors the
  // route guards). Anonymous visitors see just the public items; authenticated
  // users see any-auth items plus whatever their role unlocks.
  const visibleMenu = filterMenuByRole(menuData.children, user?.role, isAuthenticated);

  return (
    <nav className="navbar navbar-expand-xxl navbar-dark bg-primary fixed-top">
      <div className="container-fluid">
        <Link className="navbar-brand brand-wordmark" to="/" aria-label={APP_NAME}>
          Academy<em>OS</em>
        </Link>

        {/* Account / session chrome + toggler — kept OUTSIDE the collapsible
            menu so it stays reachable at every width even when the long nav
            menu overflows (order-lg-last places it at the right on desktop). */}
        <div className="d-flex align-items-center order-xxl-last ms-auto app-navbar-chrome">
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-link text-white p-0 me-3"
              aria-label="Open help"
              title="Help"
              onClick={() => setHelpOpen(true)}
            >
              <i className="fa fa-circle-question fs-5" aria-hidden="true" />
            </button>
            {isAuthenticated ? (
              <div className="dropdown">
                <a
                  className="d-flex align-items-center text-white text-decoration-none dropdown-toggle"
                  href="#"
                  id="accountMenu"
                  role="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <img
                    src="https://placehold.co/32"
                    alt=""
                    className="rounded-circle me-2"
                    width="32"
                    height="32"
                  />
                  <span className="me-1 d-none d-xl-inline text-nowrap">{user?.name || user?.email || 'Account'}</span>
                </a>
                <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="accountMenu">
                  <li>
                    <span className="dropdown-item-text text-muted small">
                      {user?.email}
                    </span>
                  </li>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li>
                    <button className="dropdown-item" type="button" onClick={logout}>
                      <i className="fa fa-right-from-bracket me-2" aria-hidden="true" />
                      Logout
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <Link className="btn btn-light btn-sm" to="/login">
                <i className="fa fa-right-to-bracket me-2" aria-hidden="true" />
                Login
              </Link>
            )}
          </div>

          <button
            className="navbar-toggler ms-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#appNavbarNav"
            aria-controls="appNavbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon" />
          </button>
        </div>

        <div className="collapse navbar-collapse" id="appNavbarNav">
          <ul className="navbar-nav me-auto mb-2 mb-xxl-0 app-navbar-menu">
            {visibleMenu.map((item) => (
              <MenuItem key={item.label} item={item} />
            ))}
          </ul>
        </div>
      </div>

      <HelpDrawer open={helpOpen} onClose={() => setHelpOpen(false)} />
    </nav>
  );
}
